import type {
  AreaUnit,
  Operation,
  PropertyField,
  PropertyStatus,
  PropertyType,
  WebLocationMode,
} from '@erp/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api } from './api.ts';

// Types mirror the API's responses (apps/api/src/properties/properties.types.ts).

export type ListingState = 'active' | 'expiring' | 'expired' | null;

export interface PropertySummary {
  id: string;
  code: string;
  title: string;
  type: PropertyType;
  operation: Operation;
  status: PropertyStatus;
  salePrice: number | null;
  rentPrice: number | null;
  currency: string;
  district: { id: number; name: string; municipality: string };
  neighborhood: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  builtArea: number | null;
  lotArea: number | null;
  lotAreaUnit: AreaUnit;
  builtAreaUnit: AreaUnit;
  assignedTo: { membershipId: string; name: string };
  exclusive: boolean;
  listingEndsOn: string | null;
  listingState: ListingState;
  webPublished: boolean;
  /** Imported listings may arrive without one (they are not published until added). */
  hasOwner: boolean;
  coverUrl: string | null;
  createdAt: string;
}

export interface PropertyMedia {
  id: string;
  kind: 'photo' | 'plan';
  status: 'processing' | 'ready' | 'failed';
  isPrivate: boolean;
  caption: string | null;
  width: number | null;
  height: number | null;
  thumbUrl: string | null;
  largeUrl: string | null;
}

export interface ShareLink {
  id: string;
  createdAt: string;
  createdBy: string;
  expiresAt: string;
  revokedAt: string | null;
  viewCount: number;
  lastViewedAt: string | null;
  active: boolean;
}

export interface PublicListing {
  code: string;
  title: string;
  description: string | null;
  type: PropertyType;
  operation: Operation;
  available: boolean;
  salePrice: number | null;
  rentPrice: number | null;
  currency: string;
  district: string;
  municipality: string;
  neighborhood: string | null;
  characteristics: Record<PropertyField, number | null>;
  lotAreaUnit: AreaUnit;
  builtAreaUnit: AreaUnit;
  videoUrl: string | null;
  virtualTourUrl: string | null;
  media: {
    id: string;
    kind: 'photo' | 'plan';
    caption: string | null;
    width: number | null;
    height: number | null;
    thumbUrl: string;
    largeUrl: string;
  }[];
  agency: { name: string };
  contact: { name: string; email: string; phone: string | null };
  expiresAt: string;
}

export interface PropertyDetail extends PropertySummary {
  description: string | null;
  cadastralCode: string | null;
  characteristics: Record<PropertyField, number | null>;
  capturedBy: { membershipId: string; name: string };
  listingStartsOn: string | null;
  commission: {
    salePct: number | null;
    rentMonths: number | null;
    networkSharePct: number | null;
    conditions: string | null;
  };
  videoUrl: string | null;
  virtualTourUrl: string | null;
  media: PropertyMedia[];
  keysCount: number | null;
  keysLocation: string | null;
  privateNotes: string | null;
  hasAddress: boolean;
  hasLocation: boolean;
  /** How the website's map shows it: by zone and district (default) or the exact point. */
  webLocationMode: WebLocationMode;
  owners: { contactId: string; name: string; sharePct: number | null }[] | null;
  ownersCount: number;
  /** English version for the website (the English site lists only these). */
  titleEn: string | null;
  descriptionEn: string | null;
  /** Imported from another system (RECONNECT): its id and type there. */
  external: { source: 'reconnect'; id: string; type: string | null } | null;
  /** Imported already sold or rented (closings arrive in Phase 3). */
  closed: { price: number | null; on: string | null } | null;
  listedAt: string | null;
  daysOnMarket: number | null;
  webPublishedAt: string | null;
  /** Featured on the website (home page carousel); only while published. */
  webFeatured: boolean;
  completeness: { publishable: boolean; percent: number; missing: string[]; blocking: string[] };
  statusHistory: {
    from: PropertyStatus | null;
    to: PropertyStatus;
    note: string | null;
    by: string | null;
    at: string;
  }[];
  priceHistory: {
    kind: 'sale' | 'rent';
    from: number | null;
    to: number | null;
    by: string | null;
    at: string;
  }[];
  createdBy: string | null;
  updatedAt: string;
  can: { edit: boolean; delete: boolean; revealSensitive: boolean; publish: boolean };
}

export interface PropertyDuplicateMatch {
  matchedBy: ('address' | 'owner' | 'cadastralCode')[];
  property: { id: string; code: string; title: string } | null;
  assignedTo: string;
}

export interface OwnedProperty {
  id: string;
  code: string;
  title: string;
  status: PropertyStatus;
  operation: Operation;
  salePrice: number | null;
  rentPrice: number | null;
  daysOnMarket: number | null;
  assignedTo: string;
}

export interface PropertyFilters {
  q?: string;
  type?: PropertyType;
  operation?: 'sale' | 'rent';
  status?: PropertyStatus;
  districtId?: number;
  priceMin?: number;
  priceMax?: number;
  bedroomsMin?: number;
  /** In the filter's unit (areaUnit); compared in m² whatever each property's unit. */
  lotAreaMin?: number;
  builtAreaMin?: number;
  areaUnit?: 'm2' | 'v2';
  withoutOwner?: 'true';
  assignedMembershipId?: string;
  exclusive?: 'true' | 'false';
  listing?: 'expiring' | 'expired';
  page?: number;
}

export const propertyKeys = {
  all: ['properties'] as const,
  list: (filters: PropertyFilters) => ['properties', 'list', filters] as const,
  detail: (id: string) => ['properties', 'detail', id] as const,
  trash: ['properties', 'trash'] as const,
  ownedBy: (contactId: string) => ['properties', 'owned-by', contactId] as const,
  shareLinks: (id: string) => ['properties', 'share-links', id] as const,
};

function toQuery(filters: PropertyFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

export const useProperties = (filters: PropertyFilters, enabled = true) =>
  useQuery({
    queryKey: propertyKeys.list(filters),
    queryFn: () =>
      api<{ total: number; items: PropertySummary[] }>(`/properties${toQuery(filters)}`),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useProperty = (id: string) =>
  useQuery({
    queryKey: propertyKeys.detail(id),
    queryFn: () => api<PropertyDetail>(`/properties/${id}`),
    retry: false,
    // While photos are being processed, check again every few seconds.
    refetchInterval: (query) =>
      query.state.data?.media.some((m) => m.status === 'processing') ? 3000 : false,
  });

export const useShareLinks = (id: string) =>
  useQuery({
    queryKey: propertyKeys.shareLinks(id),
    queryFn: () => api<ShareLink[]>(`/properties/${id}/share-links`),
  });

export const useOwnedProperties = (contactId: string) =>
  useQuery({
    queryKey: propertyKeys.ownedBy(contactId),
    queryFn: () =>
      api<{ items: OwnedProperty[]; hiddenCount: number }>(`/contacts/${contactId}/properties`),
  });

/** "$185,000" / "$950 al mes" / both, for lists and cards. */
export function priceLabel(
  p: { operation: Operation; salePrice: number | null; rentPrice: number | null },
  format: (amount: number) => string,
): string {
  const parts: string[] = [];
  if (p.salePrice !== null) parts.push(format(p.salePrice));
  if (p.rentPrice !== null) parts.push(`${format(p.rentPrice)} al mes`);
  return parts.join(' · ') || 'Sin precio';
}
